import { useState } from 'react';
import { HttpStatusCode } from 'axios';
import toast from 'react-hot-toast';
import {
  Entity,
  InsurancePremiumMode,
  PatchCustomerRequest,
} from '../../../../../../../types/apiTypes';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../../../utils/UserContext';
import {
  resolveFleetAmountCents,
  resolveInsurancePremiumMode,
} from './fleetInsurance';

export const FLEET_INSURANCE_SAVE_ERROR =
  'Error occurred while updating your insurance settings.';

/**
 * THT-675 — reads the customer's insurance premium mode off the user record and
 * persists changes through the existing customer PATCH endpoint.
 *
 * The mode is customer-level state that four surfaces read (assets tab toggle,
 * asset form, asset details, pending-truck approval), so it is derived from
 * `userRecord` rather than fetched again — `refetchUser` after a successful
 * PATCH is what makes every one of them update at once.
 */
export const useFleetInsurance = () => {
  const { userRecord, userId, refetchUser } = useUser();
  const { callProtectedApi } = useApi();
  const [isSaving, setIsSaving] = useState(false);

  const mode = resolveInsurancePremiumMode(userRecord);
  const fleetAmountCents = resolveFleetAmountCents(userRecord);
  const isCustomer = userRecord?.userType === Entity.Customer;

  /**
   * Persists mode + amount together. The amount is only sent in fleet mode:
   * per-asset mode leaves the stored cents untouched so switching back and
   * forth is lossless in both directions (spec §2, AC5).
   */
  const save = async (
    nextMode: InsurancePremiumMode,
    nextFleetAmountCents: number
  ): Promise<boolean> => {
    if (!userId) return false;
    setIsSaving(true);
    try {
      const body: PatchCustomerRequest = {
        insurance_premium_mode: nextMode,
        ...(nextMode === InsurancePremiumMode.Fleet
          ? { fleet_insurance_premium_amount_cents: nextFleetAmountCents }
          : {}),
      };
      const response = await callProtectedApi(
        `/customers/${userId}`,
        'patch',
        body,
        FLEET_INSURANCE_SAVE_ERROR
      );
      if (response?.status !== HttpStatusCode.Ok) return false;
      await refetchUser();
      toast.success('Insurance settings updated');
      return true;
    } catch (e: any) {
      console.error('Error occurred while updating insurance settings: ', e);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    mode,
    isFleetMode: mode === InsurancePremiumMode.Fleet,
    fleetAmountCents,
    /** Only a customer record carries the columns — hide the control otherwise. */
    canEditFleetInsurance: isCustomer,
    isSaving,
    save,
  };
};
